/**
 * Owner: Masa Bayu
 * File: landing-locale.ts
 * Date:Time: 2026-10-23 00:08
 * Description: Label home landing §376 (en + ms) — corak resolver sama dengan
 *   marketplace-locale; semua teks paparan di sini, tiada string dalam komponen.
 *   §466 S1 — label penarafan dibuang; harga tanpa data paparkan "Price not set".
 */

import type { MarketplaceLocaleProfile } from '../marketplace-locale';

export interface LandingLabels {
  readonly searchPlaceholder: string;
  readonly searchAria: string;
  readonly filterAria: string;
  readonly notificationsAria: string;
  readonly wishlistAria: string;
  readonly cartAria: string;
  readonly accountAria: string;
  readonly signIn: string;
  readonly memberSince: string;
  readonly sidebar: {
    readonly ariaLabel: string;
    readonly mainGroup: string;
    readonly accountGroup: string;
    readonly exploreGroup: string;
    readonly home: string;
    readonly allBooks: string;
    readonly newReleases: string;
    readonly bestsellers: string;
    readonly deals: string;
    readonly categories: string;
    readonly myLibrary: string;
    readonly myWishlist: string;
    readonly myOrders: string;
    readonly createEbook: string;
    readonly authors: string;
    readonly topics: string;
    readonly genres: string;
    readonly collections: string;
    readonly copyright: string;
    readonly terms: string;
    readonly privacy: string;
    readonly help: string;
  };
  readonly hero: {
    readonly carouselLabel: string;
    readonly eyebrowDesktop: string;
    readonly titleDesktop: string;
    readonly subDesktop: string;
    readonly scriptDesktop: string;
    readonly eyebrowMobile: string;
    readonly titleMobile: string;
    readonly subMobile: string;
    readonly browseNow: string;
    readonly previous: string;
    readonly next: string;
    readonly slidesLabel: string;
    readonly chips: ReadonlyArray<{ readonly icon: string; readonly label: string }>;
  };
  readonly categoryStrip: {
    readonly ariaLabel: string;
    readonly all: string;
    readonly more: string;
  };
  readonly sections: {
    readonly featured: string;
    readonly trending: string;
    readonly continueReading: string;
    readonly seeAll: string;
    readonly exploreTitle: string;
    readonly exploreSub: string;
    readonly bestSellers: string;
    readonly newReleases: string;
    readonly curated: string;
    readonly browseAll: string;
    readonly emptyShelf: string;
    readonly collections: {
      readonly masaBayu: string;
      readonly alamtologi: string;
    };
  };
  readonly card: {
    readonly featuredGridAria: string;
    readonly trendingGridAria: string;
    readonly continueGridAria: string;
    readonly bookmarkAdd: string;
    readonly bookmarkRemove: string;
    readonly chapter: string;
    readonly priceUnknown: string;
    readonly readNow: string;
  };
  readonly rail: {
    readonly whyTitle: string;
    readonly whyItems: ReadonlyArray<{
      readonly icon: string;
      readonly title: string;
      readonly sub: string;
    }>;
    readonly authorsTitle: string;
    readonly authorBooks: (count: number) => string;
    readonly dealsTitle: string;
    readonly dealsOff: string;
    readonly dealsSelected: string;
    readonly shopNow: string;
    readonly popularTitle: string;
    readonly popularCount: (count: number) => string;
  };
  readonly footer: {
    readonly trusted: string;
    readonly trustedSub: string;
    readonly secure: string;
    readonly secureSub: string;
    readonly support: string;
    readonly supportSub: string;
  };
  readonly tabs: {
    readonly ariaLabel: string;
    readonly home: string;
    readonly library: string;
    readonly publish: string;
    readonly notif: string;
    readonly profile: string;
  };
  readonly alerts: {
    readonly title: string;
    readonly empty: string;
  };
  readonly filterSheet: {
    readonly title: string;
    readonly all: string;
    readonly apply: string;
  };
  readonly install: {
    readonly title: string;
    readonly body: string;
    readonly cta: string;
    readonly later: string;
    readonly iosBody: string;
    readonly installing: string;
    readonly closeAria: string;
  };
}

const EN: LandingLabels = {
  searchPlaceholder: 'Search books, authors, genres, topics...',
  searchAria: 'Search',
  filterAria: 'Filter books',
  notificationsAria: 'Notifications',
  wishlistAria: 'My wishlist',
  cartAria: 'Cart',
  accountAria: 'Account',
  signIn: 'Sign in',
  memberSince: 'Member',
  sidebar: {
    ariaLabel: 'Main navigation',
    mainGroup: 'Browse',
    accountGroup: 'My account',
    exploreGroup: 'Explore',
    home: 'Home',
    allBooks: 'All Books',
    newReleases: 'New Releases',
    bestsellers: 'Bestsellers',
    deals: 'Deals & Offers',
    categories: 'Categories',
    myLibrary: 'My Library',
    myWishlist: 'My Wishlist',
    myOrders: 'My Orders',
    createEbook: 'Create eBook',
    authors: 'Authors',
    topics: 'Topics',
    genres: 'Genres',
    collections: 'Collections',
    copyright: '© 2026 QX. All rights reserved.',
    terms: 'Terms',
    privacy: 'Privacy',
    help: 'Help',
  },
  hero: {
    carouselLabel: 'Featured promotions',
    eyebrowDesktop: 'MILLIONS OF EBOOKS',
    titleDesktop: 'Discover. Read. Grow.',
    subDesktop: 'Your infinite library of knowledge, all in one place.',
    scriptDesktop: 'Better Books Brighter Minds',
    eyebrowMobile: 'YOUR NEXT GREAT READ',
    titleMobile: 'Explore a World of Knowledge',
    subMobile: 'From bestselling novels to academic books, find your next favorite ebook.',
    browseNow: 'Browse Now',
    previous: 'Previous slide',
    next: 'Next slide',
    slidesLabel: 'Promotion slides',
    chips: [
      { icon: 'bookOpen', label: 'Millions of Books' },
      { icon: 'bolt', label: 'Instant Access' },
      { icon: 'shield', label: 'Secure & Fast' },
      { icon: 'globe', label: 'Read Anywhere' },
    ],
  },
  categoryStrip: {
    ariaLabel: 'Browse by category',
    all: 'All Books',
    more: 'More',
  },
  sections: {
    featured: 'Featured Books',
    trending: 'Trending Now',
    continueReading: 'Continue Reading',
    seeAll: 'See All',
    exploreTitle: 'Explore More. Read Further.',
    exploreSub: 'Join millions of readers and discover your next favorite book.',
    bestSellers: 'Best Sellers',
    newReleases: 'New Releases',
    curated: 'Curated Collections',
    browseAll: 'Browse All Books',
    emptyShelf: 'No books in this category yet.',
    collections: {
      masaBayu: "Masa Bayu's Book Collection",
      alamtologi: 'Alamtologi Book Collection',
    },
  },
  card: {
    featuredGridAria: 'Featured books',
    trendingGridAria: 'Trending books',
    continueGridAria: 'Continue reading',
    bookmarkAdd: 'Add to wishlist',
    bookmarkRemove: 'Remove from wishlist',
    chapter: 'Chapter',
    priceUnknown: 'Price not set',
    readNow: 'Read',
  },
  rail: {
    whyTitle: 'Why QX?',
    whyItems: [
      { icon: 'bookOpen', title: 'Massive Library', sub: 'Millions of eBooks across all genres' },
      { icon: 'search', title: 'Smart Search', sub: 'AI-powered search & recommendations' },
      { icon: 'sparkle', title: 'Personalized Experience', sub: 'Curated for your interests' },
      { icon: 'download', title: 'Offline Reading', sub: 'Read anytime, anywhere' },
      { icon: 'shield', title: 'Secure & Fast', sub: 'Safe payments & instant access' },
      { icon: 'pen', title: 'Create & Publish', sub: 'Turn your ideas into eBooks' },
    ],
    authorsTitle: 'Top Authors',
    authorBooks: (count) => `${String(count)} ${count === 1 ? 'book' : 'books'}`,
    dealsTitle: 'Exclusive Deals',
    dealsOff: 'Bestsellers & offers',
    dealsSelected: 'Curated eBooks on Deals & Offers',
    shopNow: 'Shop Now',
    popularTitle: 'Popular Categories',
    popularCount: (count) => `${String(count)} ${count === 1 ? 'book' : 'books'}`,
  },
  footer: {
    trusted: 'Trusted by Millions',
    trustedSub: 'Readers worldwide',
    secure: 'Secure Payments',
    secureSub: '100% safe & encrypted',
    support: '24/7 Support',
    supportSub: "We're here to help",
  },
  tabs: {
    ariaLabel: 'Primary navigation',
    home: 'Home',
    library: 'Library',
    publish: 'Publish',
    notif: 'Notifications',
    profile: 'Profile',
  },
  alerts: {
    title: 'Notifications',
    empty: 'No notifications yet.',
  },
  filterSheet: {
    title: 'Filter by category',
    all: 'All Books',
    apply: 'Show results',
  },
  install: {
    title: 'Install QXRead',
    body: 'Add to home screen for full-screen reading — no browser bar.',
    cta: 'Install',
    later: 'Later',
    iosBody: 'Tap Share, then Add to Home Screen to install QXRead.',
    installing: 'Installing…',
    closeAria: 'Dismiss install notification',
  },
};

const MS: LandingLabels = {
  searchPlaceholder: 'Cari buku, penulis, genre, topik...',
  searchAria: 'Cari',
  filterAria: 'Penuras buku',
  notificationsAria: 'Pemberitahuan',
  wishlistAria: 'Senarai kegemaran saya',
  cartAria: 'Troli',
  accountAria: 'Akaun',
  signIn: 'Log masuk',
  memberSince: 'Ahli',
  sidebar: {
    ariaLabel: 'Navigasi utama',
    mainGroup: 'Terokai',
    accountGroup: 'Akaun saya',
    exploreGroup: 'Jelajah',
    home: 'Utama',
    allBooks: 'Semua Buku',
    newReleases: 'Terbitan Baharu',
    bestsellers: 'Jualan Terbaik',
    deals: 'Tawaran & Diskaun',
    categories: 'Kategori',
    myLibrary: 'Pustaka Saya',
    myWishlist: 'Senarai Saya',
    myOrders: 'Pesanan Saya',
    createEbook: 'Cipta eBook',
    authors: 'Penulis',
    topics: 'Topik',
    genres: 'Genre',
    collections: 'Koleksi',
    copyright: '© 2026 QX. Hak cipta terpelihara.',
    terms: 'Terma',
    privacy: 'Privasi',
    help: 'Bantuan',
  },
  hero: {
    carouselLabel: 'Promosi pilihan',
    eyebrowDesktop: 'JUTAAN EBOOK',
    titleDesktop: 'Temui. Baca. Tumbuh.',
    subDesktop: 'Pustaka ilmu tanpa had, semuanya di satu tempat.',
    scriptDesktop: 'Buku Lebih Baik, Minda Lebih Cerah',
    eyebrowMobile: 'BACAAN HEBAT ANDA SETERUSNYA',
    titleMobile: 'Jelajahi Dunia Ilmu',
    subMobile: 'Daripada novel terlaris hingga buku akademik, temui ebook kegemaran anda.',
    browseNow: 'Layari Sekarang',
    previous: 'Slaid sebelumnya',
    next: 'Slaid berikutnya',
    slidesLabel: 'Slaid promosi',
    chips: [
      { icon: 'bookOpen', label: 'Jutaan Buku' },
      { icon: 'bolt', label: 'Akses Segera' },
      { icon: 'shield', label: 'Selamat & Laju' },
      { icon: 'globe', label: 'Baca Di Mana Sahaja' },
    ],
  },
  categoryStrip: {
    ariaLabel: 'Layari ikut kategori',
    all: 'Semua Buku',
    more: 'Lagi',
  },
  sections: {
    featured: 'Buku Pilihan',
    trending: 'Trending Kini',
    continueReading: 'Teruskan Bacaan',
    seeAll: 'Lihat Semua',
    exploreTitle: 'Terokai Lagi. Baca Jauh.',
    exploreSub: 'Sertai jutaan pembaca dan temui buku kegemaran anda.',
    bestSellers: 'Jualan Terbaik',
    newReleases: 'Terbitan Baharu',
    curated: 'Koleksi Kuratori',
    browseAll: 'Layari Semua Buku',
    emptyShelf: 'Tiada buku dalam kategori ini lagi.',
    collections: {
      masaBayu: 'Koleksi Buku Masa Bayu',
      alamtologi: 'Koleksi Buku Alamtologi',
    },
  },
  card: {
    featuredGridAria: 'Buku pilihan',
    trendingGridAria: 'Buku trending',
    continueGridAria: 'Teruskan bacaan',
    bookmarkAdd: 'Tambah ke senarai',
    bookmarkRemove: 'Buang dari senarai',
    chapter: 'Bab',
    priceUnknown: 'Harga belum ditetapkan',
    readNow: 'Baca',
  },
  rail: {
    whyTitle: 'Kenapa QX?',
    whyItems: [
      { icon: 'bookOpen', title: 'Pustaka Besar', sub: 'Jutaan ebook merentas semua genre' },
      { icon: 'search', title: 'Carian Pintar', sub: 'Carian & cadangan berkuasa AI' },
      { icon: 'sparkle', title: 'Pengalaman Peribadi', sub: 'Dikurasi untuk minat anda' },
      { icon: 'download', title: 'Baca Luar Talian', sub: 'Baca bila-bila, di mana sahaja' },
      { icon: 'shield', title: 'Selamat & Laju', sub: 'Bayaran selamat & akses segera' },
      { icon: 'pen', title: 'Cipta & Terbit', sub: 'Tukar idea anda kepada eBook' },
    ],
    authorsTitle: 'Penulis Teratas',
    authorBooks: (count) => `${String(count)} buku`,
    dealsTitle: 'Tawaran Eksklusif',
    dealsOff: 'Jualan terbaik & tawaran',
    dealsSelected: 'eBook terpilih di Tawaran & Diskaun',
    shopNow: 'Beli Sekarang',
    popularTitle: 'Kategori Popular',
    popularCount: (count) => `${String(count)} buku`,
  },
  footer: {
    trusted: 'Dipercayai Jutaan',
    trustedSub: 'Pembaca di seluruh dunia',
    secure: 'Bayaran Selamat',
    secureSub: '100% selamat & disulitkan',
    support: 'Sokongan 24/7',
    supportSub: 'Kami sedia membantu',
  },
  tabs: {
    ariaLabel: 'Navigasi utama',
    home: 'Utama',
    library: 'Pustaka',
    publish: 'Terbitkan',
    notif: 'Notifikasi',
    profile: 'Profil',
  },
  alerts: {
    title: 'Pemberitahuan',
    empty: 'Tiada pemberitahuan lagi.',
  },
  filterSheet: {
    title: 'Penuras ikut kategori',
    all: 'Semua Buku',
    apply: 'Papar keputusan',
  },
  install: {
    title: 'Pasang QXRead',
    body: 'Tambah ke skrin utama untuk bacaan skrin penuh — tanpa bar pelayar.',
    cta: 'Pasang',
    later: 'Nanti',
    iosBody: 'Tekan Kongsi, kemudian Tambah ke Skrin Utama untuk pasang QXRead.',
    installing: 'Sedang dipasang…',
    closeAria: 'Tutup notifikasi pasang',
  },
};

export function resolveLandingLabels(profile: MarketplaceLocaleProfile): LandingLabels {
  return profile.language === 'ms' ? MS : EN;
}
